import React, { useState, createElement } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { toast } from 'sonner';
import { User, Shield, Bell, CreditCard, Download, Save, Eye, EyeOff } from 'lucide-react';
export function Settings() {
  const {
    state
  } = useAppContext();
  const {
    user
  } = state;
  const [activeSection, setActiveSection] = useState('profile');
  // Profile form
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  // Security
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [transactionPin, setTransactionPin] = useState('');
  const [twoFactor, setTwoFactor] = useState(false);
  // Notifications
  const [emailNotif, setEmailNotif] = useState(true);
  const [smsNotif, setSmsNotif] = useState(true);
  const [pushNotif, setPushNotif] = useState(false);
  const [transactionAlerts, setTransactionAlerts] = useState(true);
  const [campaignUpdates, setCampaignUpdates] = useState(true);
  const [rentReminders, setRentReminders] = useState(true);
  // Bank accounts
  const [bankAccounts] = useState([{
    id: '1',
    bank: 'GTBank',
    accountNumber: '0123456789',
    accountName: 'Tunde Adeleke'
  }]);
  const sections = [{
    id: 'profile',
    label: 'Profile',
    icon: User
  }, {
    id: 'security',
    label: 'Security',
    icon: Shield
  }, {
    id: 'notifications',
    label: 'Notifications',
    icon: Bell
  }, {
    id: 'bank-accounts',
    label: 'Bank Accounts',
    icon: CreditCard
  }, {
    id: 'data',
    label: 'Data & Export',
    icon: Download
  }];
  const handleSaveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success('Profile updated successfully');
  };
  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    toast.success('Password changed successfully');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };
  const handleSetPin = (e: React.FormEvent) => {
    e.preventDefault();
    if (transactionPin.length !== 4 || !/^\d+$/.test(transactionPin)) {
      toast.error('PIN must be exactly 4 digits');
      return;
    }
    toast.success('Transaction PIN set successfully');
    setTransactionPin('');
  };
  const handleExportData = () => {
    const data = JSON.stringify(state, null, 2);
    const blob = new Blob([data], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'billvast-data-export.json';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Data exported successfully');
  };
  const ToggleSwitch = ({
    enabled,
    onChange
  }: {
    enabled: boolean;
    onChange: (v: boolean) => void;
  }) => <button type="button" onClick={() => onChange(!enabled)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-brand-primary' : 'bg-gray-200'}`} data-id="element-856">
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-857" />
    </button>;
  return <div className="space-y-6" data-id="element-858">
      <div data-id="element-859">
        <h1 className="text-2xl font-bold text-brand-text-dark" data-id="element-860">Settings</h1>
        <p className="text-brand-text-light mt-1" data-id="element-861">
          Manage your account preferences and security.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6" data-id="element-862">
        {/* Sidebar Nav */}
        <div className="lg:w-56 shrink-0" data-id="element-863">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0" data-id="element-864">
            {sections.map(section => {
            const Icon = section.icon;
            return <button key={section.id} onClick={() => setActiveSection(section.id)} className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${activeSection === section.id ? 'bg-brand-primary/10 text-brand-primary' : 'text-brand-text-light hover:bg-gray-50 hover:text-brand-text-dark'}`} data-id="element-865">
                  <Icon size={18} data-id="element-866" />
                  {section.label}
                </button>;
          })}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0" data-id="element-867">
          {/* Profile Section */}
          {activeSection === 'profile' && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-868">
              <h2 className="text-lg font-semibold text-brand-text-dark mb-6" data-id="element-869">
                Profile Information
              </h2>
              <form onSubmit={handleSaveProfile} className="space-y-5 max-w-lg" data-id="element-870">
                <div className="flex items-center gap-4 mb-6" data-id="element-871">
                  <img src={user?.avatar || 'https://ui-avatars.com/api/?name=User&background=73C883&color=fff'} alt="Avatar" className="w-16 h-16 rounded-full" data-id="element-872" />
                  <div data-id="element-873">
                    <p className="font-semibold text-brand-text-dark" data-id="element-874">
                      {user?.name}
                    </p>
                    <p className="text-sm text-brand-text-light" data-id="element-875">
                      {user?.email}
                    </p>
                  </div>
                </div>

                <div data-id="element-876">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-877">
                    Full Name
                  </label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" data-id="element-878" />
                </div>
                <div data-id="element-879">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-880">
                    Email Address
                  </label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" data-id="element-881" />
                </div>
                <div data-id="element-882">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-883">
                    Phone Number
                  </label>
                  <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" data-id="element-884" />
                </div>
                <button type="submit" className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand-primary text-white rounded-xl font-medium hover:bg-brand-primary-medium transition-colors" data-id="element-885">
                  <Save size={18} data-id="element-886" /> Save Changes
                </button>
              </form>
            </div>}

          {/* Security Section */}
          {activeSection === 'security' && <div className="space-y-6" data-id="element-887">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-888">
                <h2 className="text-lg font-semibold text-brand-text-dark mb-6" data-id="element-889">
                  Change Password
                </h2>
                <form onSubmit={handleChangePassword} className="space-y-4 max-w-lg" data-id="element-890">
                  <div data-id="element-891">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-892">
                      Current Password
                    </label>
                    <div className="relative" data-id="element-893">
                      <input type={showPassword ? 'text' : 'password'} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none pr-10" data-id="element-894" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-895">
                        {showPassword ? <EyeOff size={18} data-id="element-896" /> : <Eye size={18} data-id="element-897" />}
                      </button>
                    </div>
                  </div>
                  <div data-id="element-898">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-899">
                      New Password
                    </label>
                    <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" data-id="element-900" />
                  </div>
                  <div data-id="element-901">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-902">
                      Confirm New Password
                    </label>
                    <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" data-id="element-903" />
                  </div>
                  <button type="submit" className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand-primary text-white rounded-xl font-medium hover:bg-brand-primary-medium transition-colors" data-id="element-904">
                    Update Password
                  </button>
                </form>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-905">
                <h2 className="text-lg font-semibold text-brand-text-dark mb-6" data-id="element-906">
                  Transaction PIN
                </h2>
                <form onSubmit={handleSetPin} className="space-y-4 max-w-lg" data-id="element-907">
                  <div data-id="element-908">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-909">
                      Set 4-Digit PIN
                    </label>
                    <input type="password" value={transactionPin} onChange={e => setTransactionPin(e.target.value.slice(0, 4))} maxLength={4} placeholder="••••" className="w-32 px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none text-center text-xl tracking-widest" data-id="element-910" />
                  </div>
                  <button type="submit" className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand-primary text-white rounded-xl font-medium hover:bg-brand-primary-medium transition-colors" data-id="element-911">
                    Set PIN
                  </button>
                </form>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-912">
                <div className="flex items-center justify-between max-w-lg" data-id="element-913">
                  <div data-id="element-914">
                    <h2 className="text-lg font-semibold text-brand-text-dark" data-id="element-915">
                      Two-Factor Authentication
                    </h2>
                    <p className="text-sm text-brand-text-light mt-1" data-id="element-916">
                      Add an extra layer of security to your account.
                    </p>
                  </div>
                  <ToggleSwitch enabled={twoFactor} onChange={v => {
                setTwoFactor(v);
                toast.success(v ? '2FA enabled' : '2FA disabled');
              }} data-id="element-917" />
                </div>
              </div>
            </div>}

          {/* Notifications Section */}
          {activeSection === 'notifications' && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-918">
              <h2 className="text-lg font-semibold text-brand-text-dark mb-6" data-id="element-919">
                Notification Preferences
              </h2>
              <div className="space-y-6 max-w-lg" data-id="element-920">
                <div className="space-y-4" data-id="element-921">
                  <h3 className="text-sm font-semibold text-brand-text-dark uppercase tracking-wider" data-id="element-922">
                    Channels
                  </h3>
                  <div className="flex items-center justify-between py-2" data-id="element-923">
                    <div data-id="element-924">
                      <p className="font-medium text-brand-text-dark" data-id="element-925">
                        Email Notifications
                      </p>
                      <p className="text-sm text-brand-text-light" data-id="element-926">
                        Receive updates via email
                      </p>
                    </div>
                    <ToggleSwitch enabled={emailNotif} onChange={setEmailNotif} data-id="element-927" />
                  </div>
                  <div className="flex items-center justify-between py-2" data-id="element-928">
                    <div data-id="element-929">
                      <p className="font-medium text-brand-text-dark" data-id="element-930">
                        SMS Notifications
                      </p>
                      <p className="text-sm text-brand-text-light" data-id="element-931">
                        Receive updates via text message
                      </p>
                    </div>
                    <ToggleSwitch enabled={smsNotif} onChange={setSmsNotif} data-id="element-932" />
                  </div>
                  <div className="flex items-center justify-between py-2" data-id="element-933">
                    <div data-id="element-934">
                      <p className="font-medium text-brand-text-dark" data-id="element-935">
                        Push Notifications
                      </p>
                      <p className="text-sm text-brand-text-light" data-id="element-936">
                        Browser push notifications
                      </p>
                    </div>
                    <ToggleSwitch enabled={pushNotif} onChange={setPushNotif} data-id="element-937" />
                  </div>
                </div>

                <hr className="border-gray-100" data-id="element-938" />

                <div className="space-y-4" data-id="element-939">
                  <h3 className="text-sm font-semibold text-brand-text-dark uppercase tracking-wider" data-id="element-940">
                    Alert Types
                  </h3>
                  <div className="flex items-center justify-between py-2" data-id="element-941">
                    <div data-id="element-942">
                      <p className="font-medium text-brand-text-dark" data-id="element-943">
                        Transaction Alerts
                      </p>
                      <p className="text-sm text-brand-text-light" data-id="element-944">
                        Get notified for every transaction
                      </p>
                    </div>
                    <ToggleSwitch enabled={transactionAlerts} onChange={setTransactionAlerts} data-id="element-945" />
                  </div>
                  <div className="flex items-center justify-between py-2" data-id="element-946">
                    <div data-id="element-947">
                      <p className="font-medium text-brand-text-dark" data-id="element-948">
                        Campaign Updates
                      </p>
                      <p className="text-sm text-brand-text-light" data-id="element-949">
                        Updates on campaigns you support
                      </p>
                    </div>
                    <ToggleSwitch enabled={campaignUpdates} onChange={setCampaignUpdates} data-id="element-950" />
                  </div>
                  <div className="flex items-center justify-between py-2" data-id="element-951">
                    <div data-id="element-952">
                      <p className="font-medium text-brand-text-dark" data-id="element-953">
                        Rent Reminders
                      </p>
                      <p className="text-sm text-brand-text-light" data-id="element-954">
                        Get reminded before rent is due
                      </p>
                    </div>
                    <ToggleSwitch enabled={rentReminders} onChange={setRentReminders} data-id="element-955" />
                  </div>
                </div>

                <button onClick={() => toast.success('Notification preferences saved')} className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand-primary text-white rounded-xl font-medium hover:bg-brand-primary-medium transition-colors" data-id="element-956">
                  <Save size={18} data-id="element-957" /> Save Preferences
                </button>
              </div>
            </div>}

          {/* Bank Accounts Section */}
          {activeSection === 'bank-accounts' && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-958">
              <h2 className="text-lg font-semibold text-brand-text-dark mb-6" data-id="element-959">
                Linked Bank Accounts
              </h2>
              <div className="space-y-4 max-w-lg" data-id="element-960">
                {bankAccounts.map(account => <div key={account.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl" data-id="element-961">
                    <div className="flex items-center gap-3" data-id="element-962">
                      <div className="w-10 h-10 bg-brand-primary/10 rounded-full flex items-center justify-center text-brand-primary" data-id="element-963">
                        <CreditCard size={20} data-id="element-964" />
                      </div>
                      <div data-id="element-965">
                        <p className="font-medium text-brand-text-dark" data-id="element-966">
                          {account.bank}
                        </p>
                        <p className="text-sm text-brand-text-light" data-id="element-967">
                          {account.accountNumber} — {account.accountName}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs bg-brand-primary/10 text-brand-primary px-2 py-1 rounded-full font-medium" data-id="element-968">
                      Primary
                    </span>
                  </div>)}
                <button onClick={() => toast.info('Add bank account feature coming soon')} className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-brand-text-light hover:border-brand-primary hover:text-brand-primary transition-colors font-medium" data-id="element-969">
                  + Add Bank Account
                </button>
              </div>
            </div>}

          {/* Data Export Section */}
          {activeSection === 'data' && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-970">
              <h2 className="text-lg font-semibold text-brand-text-dark mb-2" data-id="element-971">
                Data & Export
              </h2>
              <p className="text-brand-text-light mb-6" data-id="element-972">
                Download a copy of all your Billvast data.
              </p>
              <div className="space-y-4 max-w-lg" data-id="element-973">
                <div className="p-4 bg-gray-50 rounded-xl" data-id="element-974">
                  <h3 className="font-medium text-brand-text-dark mb-1" data-id="element-975">
                    Export All Data (JSON)
                  </h3>
                  <p className="text-sm text-brand-text-light mb-3" data-id="element-976">
                    Includes your profile, transactions, campaigns, transfers,
                    and property data.
                  </p>
                  <button onClick={handleExportData} className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-primary text-white rounded-xl font-medium hover:bg-brand-primary-medium transition-colors" data-id="element-977">
                    <Download size={18} data-id="element-978" /> Download Data
                  </button>
                </div>

                <div className="p-4 bg-red-50 rounded-xl" data-id="element-979">
                  <h3 className="font-medium text-brand-error mb-1" data-id="element-980">
                    Delete Account
                  </h3>
                  <p className="text-sm text-brand-text-light mb-3" data-id="element-981">
                    Permanently delete your account and all associated data.
                    This action cannot be undone.
                  </p>
                  <button onClick={() => toast.error('Account deletion is disabled in demo mode')} className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-error text-white rounded-xl font-medium hover:bg-red-600 transition-colors" data-id="element-982">
                    Delete Account
                  </button>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}